/**
 * Report operations the taskpane drives, each owning its own `Excel.run` so `ui/` owns none.
 *
 * Office batches cannot nest or run concurrently. Keeping every batch on this side means the
 * ordering constraints are visible together rather than spread across cards and hooks.
 */
import Logger from "../../../clients/sentry";
import { serializeErrorForWarning } from "../../../errors";
import { getSheetsByReportType, checkIfSheetWithReportTypeExists } from "../../workbook/customProperties";
import { generateMultiSheetSubheader } from "./index";

/** The "N sheets" line under a multi-sheet report's title. */
export const readMultiSheetSubheader = async (splitByKey: string, reportName: string): Promise<string> =>
  Excel.run(async (context) => generateMultiSheetSubheader(context, splitByKey, reportName));

/**
 * Brings the report's first sheet forward and reads its subheader back, in one batch — the caller
 * has just regenerated it and needs both.
 */
export const activateReportAndReadSubheader = async (reportName: string, splitByKey: string): Promise<string> =>
  Excel.run(async (context) => {
    const reports = await getSheetsByReportType(context, reportName);
    if (reports.length > 0) {
      try {
        reports[0].worksheet.activate();
        await context.sync();
      } catch (error) {
        Logger.warn("Could not activate the report sheet", { reportName, ...serializeErrorForWarning(error) });
      }
    }
    return generateMultiSheetSubheader(context, splitByKey, reportName);
  });

/**
 * Runs a single-sheet report against an existing worksheet and activates it.
 * Returns false when the worksheet is gone, so the caller can say so — this does not show copy.
 */
export const runReportOnWorksheet = async (
  worksheetName: string,
  runner: (worksheet: Excel.Worksheet) => Promise<void>
): Promise<boolean> =>
  Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();
    if (worksheet.isNullObject) return false;

    await runner(worksheet);
    await context.sync();
    try {
      worksheet.activate();
      await context.sync();
    } catch (error) {
      Logger.warn("Could not activate the report sheet", { worksheetName, ...serializeErrorForWarning(error) });
    }
    return true;
  });

/** Deletes every sheet carrying this report type, falling back to the name for pre-property sheets. */
export const deleteReportSheets = async (reportName: string, worksheetName: string): Promise<void> =>
  Excel.run(async (context) => {
    const sheets = await getSheetsByReportType(context, reportName);
    if (sheets.length > 0) {
      sheets.forEach((sheet) => sheet.worksheet.delete());
    } else {
      // Report sheets predating the custom property are only findable by name.
      context.workbook.worksheets.getItemOrNullObject(worksheetName).delete();
    }
    await context.sync();
  });

/**
 * The given names that are defined on the worksheet, in one batch. A missing worksheet, or a probe
 * that throws, yields none — the caller only uses this to disable controls that would do nothing.
 */
export const namedRangesOnWorksheet = async (worksheetName: string, names: readonly string[]): Promise<Set<string>> =>
  Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();
    if (worksheet.isNullObject) return new Set<string>();

    const items = names.map((name) => worksheet.names.getItemOrNullObject(name));
    await context.sync();
    return new Set(names.filter((_, i) => !items[i].isNullObject));
  }).catch(() => new Set<string>());

/**
 * Whether a sheet already exists for each report type, in one batch. A probe that throws counts as
 * absent — the caller uses this to decide what to offer, and offering too much beats offering none.
 */
export const reportTypesExist = async (reportNames: readonly string[]): Promise<boolean[]> =>
  Excel.run(async (context) => {
    const results: boolean[] = [];
    for (const name of reportNames) {
      results.push(await checkIfSheetWithReportTypeExists(context, name).catch(() => false));
    }
    return results;
  }).catch(() => reportNames.map(() => false));
